import {UI} from "../../../../stem-core/ui/UIBase";
import {styleRule, styleRuleInherit, StyleSheet} from "../../../../stem-core/ui/Style";
import {registerStyle} from "../../../../stem-core/ui/style/Theme";
import {BaseInputElement} from "../../../../stem-core/ui/input/BaseInputElement.js";
import {BlinkButton} from "../../Button.jsx";
import {BUTTON_TYPE} from "../../../../blinkpay/Constants.js";
import {Money} from "../../../../stem-core/localization/Money.js";
import {MerchantCurrencyInput, MerchantCurrencyInputStyle} from "../../../../blinkpay/common/MerchantCurrencyInput.jsx";
import {Messages} from "../../../../blinkpay/Messages.js";
import {isNumericString} from "../../../../stem-core/base/Utils.js";


export class AmountRadioButtonsInputStyle extends StyleSheet {
    GAP_SIZE = 10;

    @styleRule
    amountContainer = {
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "space-evenly",
        gap: this.GAP_SIZE + "px",
        width: "100%",
    }

    @styleRule
    button = {
        "--buttons-per-row": 3,
        "--gap": this.GAP_SIZE + "px",
        flex: "0 0 calc((100% / var(--buttons-per-row)) - var(--gap))",
        minWidth: 80,
    }

    @styleRule
    errorMessage = {
        textAlign: "center",
        color: this.themeProps.ERROR_COLOR,
        height: "1em",
        marginTop: 8,
    }
}

class CustomAmountInputStyle extends MerchantCurrencyInputStyle {
    @styleRuleInherit
    container = {
        marginTop: 0,
        marginBottom: 0,
        height: "100%",
        background: "transparent",
        color: "inherit",
        border: null,
        ":focus": null
    }

    @styleRuleInherit
    inputContainer = {
        height: "100%",
        border: 0,
        borderBottom: 0,
        color: "inherit",
        background: "transparent",
        ":focus": null,
        ":hover": null,
    }

    @styleRuleInherit
    input = {
        height: "100%",
        padding: 0,
        color: "inherit",
        caretColor: "inherit",
        background: "transparent",
        ":focus": null,
    };

}

@registerStyle(AmountRadioButtonsInputStyle)
export class AmountRadioButtonsInput extends BaseInputElement {
    activeIndex = null;
    error = null;

    getDefaultOptions() {
        return {
            amounts: [], // TODO @branch should be values
            initialValue: null,
            currency: null,
        }
    }

    formatAmount(amount) {
        return new Money(amount, this.options.currency);
    }

    setActiveIndex(activeIndex) {
        this.activeIndex = activeIndex;
        this.redraw();
    }

    getActiveIndex() {
        const {activeIndex} = this;
        const {amounts} = this.options;
        const currentValue = this.getValue();
        if (activeIndex) {
            return activeIndex;
        }

        for (let index = 0; index < amounts.length; index++) {
            if (amounts[index] === currentValue) {
                return index;
            }
        }

        // Handle if we should be initialized with a custom amount
        for (let index = 0; index < amounts.length; index++) {
            if (amounts[index] == null) {
                return index;
            }
        }

        return 0;
    }

    renderErrorMessage() {
        const {styleSheet} = this;

        return <div className={styleSheet.errorMessage}>
            {this.error}
        </div>
    }

    renderCustomAmountButton(buttonType, index) {
        const {styleSheet} = this;
        const {currency} = this.options;
        const {minAmount, maxAmount} = this.options.customAmountOptions;

        return <BlinkButton
            className={styleSheet.button}
            type={buttonType}
            onClick={() => {
                this.setActiveIndex(index);
                const value = currency.mainUnitsToAmount(this.customAmountInput?.getValue());
                if (value && !this.error) {
                    this.setValue(value);
                }
            }}
        >
            <MerchantCurrencyInput // TODO @branch take care of currency logic in the input class
                styleSheet={CustomAmountInputStyle}
                ref="customAmountInput"
                inputAttributes={{
                    min: currency.amountToMainUnits(minAmount),
                    max: currency.amountToMainUnits(maxAmount),
                }}
                currency={this.options.currency}
                onChange={(value) => {
                    if (this.error) {
                        // If there's an error, simply ignore the new value
                        // TODO @input The input itself should be aware of this
                        return;
                    }
                    value = currency.mainUnitsToAmount(value);  // TODO @Money this is stupid, the currency input should return a money already
                    this.setValue(value);
                }}
                onValidInput={() => {
                    this.error = null;
                    this.redraw();
                }}
                onInvalidInput={(rawValue) => {
                    const newInvalidError = Messages.amountInvalidError(currency.amountToMainUnits(minAmount), currency.amountToMainUnits(maxAmount));
                    this.error = newInvalidError;
                    this.redraw();

                    setTimeout(() => {
                        if (this.error !== newInvalidError) {
                            return;
                        }
                        this.error = null;
                        this.redraw();
                    }, 5000);

                    if (!isNumericString(rawValue)) {
                        // Keep existing value
                        return false;
                    }
                    return parseFloat(rawValue) <= currency.amountToMainUnits(maxAmount);
                }}
            />
        </BlinkButton>
    }

    renderAmountOptions() {
        const {styleSheet} = this;
        const {amounts} = this.options;
        const activeIndex = this.getActiveIndex();

        return amounts.map((amount, index) => {
            const buttonType = index === activeIndex ? BUTTON_TYPE.PRIMARY : BUTTON_TYPE.SECONDARY;

            if (amount == null) {
                return this.renderCustomAmountButton(buttonType, index);
            }

            return <BlinkButton
                className={styleSheet.button}
                label={this.formatAmount(amount)}
                type={buttonType}
                onClick={() => {
                    this.setValue(amount);
                    this.setActiveIndex(index);
                }}
            />;
        });
    }

    render() {
        const {styleSheet} = this;

        return [
            <div className={styleSheet.amountContainer}>
                {this.renderAmountOptions()}
            </div>,
            this.renderErrorMessage(),
        ];
    }
}